import React, { useEffect, useState, useRef, useMemo } from 'react';
import JSZip from 'jszip';
import axios from 'axios';
import { toast } from 'react-toastify';
import useFetchNetsuiteDropdown from '../../../hooks/useFetchNetsuiteDropdown';
import ConfirmDialog from '../../UserDialog/ConfirmDialog';
import ValidateDialog from '../../UserDialog/ValidateDialog';
import { useNetSuiteProductionRfq } from '../../../config/netsuite';

import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';

import {
  ClientSideRowModelModule,
  RowGroupingModule,
  MenuModule,
  ColumnsToolPanelModule,
  ExcelExportModule,
  ClipboardModule,
  AggregationModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  FiltersToolPanelModule,
  SideBarModule,
  RowStyleModule,
  RowSelectionModule,
  RichSelectModule,
  SelectEditorModule,
  ClientSideRowModelApiModule,
} from 'ag-grid-enterprise';

import { ModuleRegistry } from 'ag-grid-community';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowGroupingModule,
  MenuModule,
  ColumnsToolPanelModule,
  ExcelExportModule,
  ClipboardModule,
  AggregationModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  FiltersToolPanelModule,
  SideBarModule,
  RowStyleModule,
  RowSelectionModule,
  RichSelectModule,
  SelectEditorModule,
  ClientSideRowModelApiModule,
]);

const RfqBidSubmissionReviewModal = ({ open, onClose, rfqId, onSubmitForApproval }) => {
  const [header, setHeader] = useState(null);
  const { taxCodes } = useFetchNetsuiteDropdown();

  const [originalBidLines, setOriginalBidLines] = useState([]);
  const [bidLines, setBidLines] = useState([]);
  const [rfqPoLines, setRfqPoLines] = useState([]);

  const [vendors, setVendors] = useState([]);

  const [submitConfirmDialogOpen, setSubmitConfirmDialogOpen] = useState(false);
  const [totalAwardedAmountWithTax, setTotalAwardedAmountWithTax] = useState(0);

  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [validationMessages, setValidationMessages] = useState([]);

  const [gridChanged, setGridChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const gridRef = useRef();

  useEffect(() => {
    if (!open || !rfqId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [headerRes, bidRes, polinesRes] = await Promise.all([
          axios.get(`/api/RfqHeader/rfq?rfqId=${rfqId}`),
          axios.get(`/api/VendorAssignedRfq/RfqDetails/Dataset/${rfqId}`),
          axios.get(`/api/PurchaseOrderLineItems/po-lineitems?rfqId=${rfqId}`),
        ]);

        const headerData = headerRes.data?.[0];
        if (headerData) setHeader(headerData);
        const bidData = bidRes.data || [];

        const rows =
          bidData
            .sort((a, b) => (a.poliLineItemNumber ?? 0) - (b.poliLineItemNumber ?? 0))
            .map((item) => ({
              ...item,
              vendorIsAwarded: item.vendorIsAwarded ?? false,
              awardedVendorQuantity: item.awardedVendorQuantity ?? 0,
              taxRate: item.taxRate ?? 0,
              taxCode: item.taxCode ?? '',
            })) || [];

        const uniqueVendorsMap = new Map();
        for (const line of rows) {
          if (line.nsVendorId && line.nsVendorName && !uniqueVendorsMap.has(line.nsVendorId)) {
            uniqueVendorsMap.set(line.nsVendorId, {
              vendorId: line.nsVendorId,
              vendorName: line.nsVendorName,
            });
          }
        }

        const sortedPoLines = [...polinesRes.data].sort((a, b) => (a.lineItemNumber ?? 0) - (b.lineItemNumber ?? 0));
        setRfqPoLines(sortedPoLines);

        setBidLines(rows);
        setOriginalBidLines(JSON.parse(JSON.stringify(rows)));
        setTimeout(() => {
          gridRef.current.api.forEachNode((node) => {
            if (node.data?.vendorIsAwarded) {
              node.setSelected(true);
            }
          });
        }, 0);
        setVendors(Array.from(uniqueVendorsMap.values()));
        setTotalAwardedAmountWithTax(calculateTotalAwardedWithTax(rows));
      } catch (err) {
        toast.error('Failed to load RFQ bid sheet.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      setRfqPoLines([]);
      setBidLines([]);
      setOriginalBidLines([]);
      setHeader(null);
      setVendors([]);
      setTotalAwardedAmountWithTax(0);
      setGridChanged(false);
    };
  }, [open, rfqId]);

  const handleFirstDataRendered = () => {
    if (!gridRef.current) return;

    gridRef.current.api.forEachNode((node) => {
      if (node.data?.vendorIsAwarded) {
        node.setSelected(true);
      }
    });
  };

  const handleDownloadAttachments = async () => {
    try {
      if (!rfqId) return;

      const zip = new JSZip();

      for (const vendor of vendors) {
        const { vendorId, vendorName } = vendor;

        const { data: vendorFiles } = await axios.get(`/api/RfqHeader/fetchAttachments/${rfqId}?vendorId=${vendorId}`);
        if (vendorFiles.length === 0) continue;

        const vendorFolder = zip.folder(vendorName);
        for (const file of vendorFiles) {
          const blobResponse = await axios.get(
            `/api/RfqHeader/GetBlobFileContent?blobItemPath=${encodeURIComponent(file.blobName)}`,
            {
              responseType: 'blob',
            }
          );

          const fileName = file.fileName.split('/').pop();
          vendorFolder.file(fileName, blobResponse.data, { binary: true });
        }
      }

      const { data: internalFiles } = await axios.get(`/api/RfqHeader/fetchAttachments/${rfqId}`);
      if (internalFiles.length > 0) {
        const internalFolder = zip.folder('Internal_Files');

        for (const file of internalFiles) {
          const blobResponse = await axios.get(
            `/api/RfqHeader/GetBlobFileContent?blobItemPath=${encodeURIComponent(file.blobName)}`,
            {
              responseType: 'blob',
            }
          );

          const fileName = file.fileName.split('/').pop();
          internalFolder.file(fileName, blobResponse.data, { binary: true });
        }
      }

      // 4. Generate and trigger download
      const content = await zip.generateAsync({ type: 'blob' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = `rfq_${header.description}_attachments.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download attachments:', err);
      toast.error('Attachment download failed.');
    }
  };

  const handleCellChange = (params) => {
    const { data, colDef } = params;
    const field = colDef.field;

    if (field === 'vendorIsAwarded') {
      data.vendorIsAwarded = params.newValue;
      data.awardedVendorQuantity = params.newValue ? (data.vendorQuantity ?? 0) : 0;
    }

    if (field === 'taxCode') {
      const selected = taxCodes.find((tc) => tc.value === params.newValue);
      data.taxCode = selected?.value ?? '';
      data.taxRate = selected?.rate ?? 0;
    }

    if (field === 'awardedVendorQuantity') {
      data.awardedVendorQuantity = parseFloat(params.newValue) || 0;
    }

    setGridChanged(true);

    setTimeout(() => {
      params.api.refreshCells({ force: true });
      params.api.refreshClientSideRowModel('aggregate');

      const updatedData = [];
      params.api.forEachNode((node) => {
        if (node.data) updatedData.push(node.data);
      });
      setTotalAwardedAmountWithTax(calculateTotalAwardedWithTax(updatedData));
    }, 0);
  };

  const handleSelectionChanged = () => {
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    let hasChanges = false;

    gridRef.current.api.forEachNode((node) => {
      if (!node.group && node.data) {
        const shouldBeAwarded = selectedNodes.includes(node);
        const awardedQty = shouldBeAwarded ? (node.data.vendorQuantity ?? 0) : 0;

        if (node.data.vendorIsAwarded !== shouldBeAwarded || node.data.awardedVendorQuantity !== awardedQty) {
          node.data.vendorIsAwarded = shouldBeAwarded;
          node.data.awardedVendorQuantity = awardedQty;
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setGridChanged(true);
      gridRef.current.api.refreshCells({ force: true });
      gridRef.current.api.refreshClientSideRowModel('aggregate');

      const allData = [];
      gridRef.current.api.forEachNode((node) => {
        if (node.data) {
          allData.push(node.data);
        }
      });
      setTotalAwardedAmountWithTax(calculateTotalAwardedWithTax(allData));
    }
  };

  const calculateTotalAwardedWithTax = (lines) => {
    return (lines || [])
      .filter((line) => line && line.vendorIsAwarded)
      .reduce((acc, line) => {
        const ext = Number(line.vendorUnitPrice) * Number(line.awardedVendorQuantity);
        const tax = ext * Number(line.taxRate);
        return acc + ext + tax;
      }, 0);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Vendor',
        field: 'nsVendorName',
        rowGroup: true,
        hide: true,
        valueFormatter: (params) => {
          if (!params.node.group) {
            return '';
          }
        },
      },
      { headerName: 'id', field: 'vendorLineItemId', hide: true },
      { headerName: 'PO Line Item ID', field: 'poLineItemId', hide: true },
      { headerName: 'Line #', field: 'poliLineItemNumber', hide: true },
      { headerName: 'Award', field: 'vendorIsAwarded', hide: true },
      { headerName: 'Line Item Description', field: 'poliDescription', wrapText: true, autoHeight: true },
      { headerName: 'Line Item Comments', field: 'comments', wrapText: true, autoHeight: true },
      { headerName: 'Desired Quantity', field: 'poliQuantity' },
      { headerName: 'Bidded Quantity', field: 'vendorQuantity' },
      {
        headerName: 'Awarded Quantity',
        field: 'awardedVendorQuantity',
        editable: true,
        cellEditor: 'agNumberCellEditor',
        valueSetter: (params) => {
          const newVal = parseFloat(params.newValue);
          if (isNaN(newVal)) return false;

          const max = params.data.vendorQuantity ?? 0;
          const clampedValue = Math.min(newVal, max);
          params.data.awardedVendorQuantity = clampedValue;

          return true;
        },
        cellStyle: (params) => {
          const { data, node } = params;
          if (node.group || !data?.vendorIsAwarded) {
            return { backgroundColor: 'transparent' };
          }

          const desired = Number(data.poliQuantity ?? 0);
          const awarded = Number(data.awardedVendorQuantity ?? 0);

          if (awarded === 0) return { backgroundColor: 'transparent' };
          return desired !== awarded ? { backgroundColor: '#fff3cd' } : { backgroundColor: 'transparent' };
        },
      },
      //{ headerName: 'UOM', field: 'poliUom' },
      { headerName: 'Unit Price', field: 'vendorUnitPrice', valueFormatter: priceFormatter },
      {
        headerName: 'Awarded Ext Price',
        field: 'vendorExtPrice',
        valueGetter: ({ data }) => data.awardedVendorQuantity * data.vendorUnitPrice || 0,
        valueFormatter: priceFormatter,
        aggFunc: 'sum',
      },
      {
        headerName: 'Tax Rate',
        field: 'taxCode',
        editable: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: taxCodes.filter((tc) => typeof tc.rate === 'number' && !isNaN(tc.rate)).map((tc) => tc.value),
          formatValue: (value) => {
            const match = taxCodes.find((tc) => tc.value === value);
            return match ? `(${(match.rate * 100).toFixed(2)}%)` : '';
          },
        },
        valueFormatter: (params) => {
          const match = taxCodes.find((tc) => tc.value === params.value);
          return match && typeof match.rate === 'number' ? `(${(match.rate * 100).toFixed(2)}%)` : '';
        },
        cellRendererSelector: (params) => {
          if (params.node.group) return { component: 'agGroupCellRenderer' };
          return null;
        },
      },
      {
        headerName: 'Tax Amount',
        field: 'taxAmount',
        valueGetter: ({ data }) => {
          if (!data.vendorIsAwarded) return 0;
          const ext = Number(data.vendorUnitPrice) * Number(data.awardedVendorQuantity);
          const tax = ext * Number(data.taxRate);
          return isNaN(tax) ? 0 : tax;
        },
        valueFormatter: priceFormatter,
        aggFunc: 'sum',
      },
      {
        headerName: 'Total + Tax',
        field: 'awardedPlusTax',
        valueGetter: ({ data }) => {
          const ext = Number(data.vendorUnitPrice) * Number(data.awardedVendorQuantity);
          const tax = ext * Number(data.taxRate);
          return ext + tax;
        },
        valueFormatter: priceFormatter,
        aggFunc: 'sum',
      },
      { headerName: 'RFQ Vendor Comments', field: 'vendorComments', wrapText: true, autoHeight: true, hide: true },
    ],
    [taxCodes]
  );

  const groupRowRendererParams = useMemo(() => {
    return {
      suppressCount: true,
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: 'Award Selection',
      minWidth: 250,
      cellRendererParams: {
        suppressCount: true,
      },
    };
  }, []);

  const rowSelection = useMemo(() => {
    return {
      mode: 'multiRow',
      checkboxLocation: 'autoGroupColumn',
      groupSelects: 'descendants',
    };
  }, []);

  const hasAnyAwarded = useMemo(() => {
    return bidLines.some((line) => line.vendorIsAwarded);
  }, [bidLines]);

  function priceFormatter(params) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(params.value || 0);
  }

  const handleSaveChanges = async () => {
    try {
      const changedLines = bidLines.filter((current) => {
        const original = originalBidLines.find((o) => o.vendorLineItemId === current.vendorLineItemId);
        if (!original) return true; // new line or mismatch

        return (
          original.vendorIsAwarded !== current.vendorIsAwarded ||
          original.awardedVendorQuantity !== current.awardedVendorQuantity ||
          original.taxCode !== current.taxCode ||
          original.taxRate !== current.taxRate
        );
      });

      if (changedLines.length === 0) {
        toast.info('No changes to save.');
        return;
      }

      const patchPromises = changedLines.map((line) => {
        const patchPayload = [
          { op: 'replace', path: '/isAwarded', value: line.vendorIsAwarded },
          { op: 'replace', path: '/awardedVendorQuantity', value: line.awardedVendorQuantity ?? 0 },
          { op: 'replace', path: '/taxCode', value: line.taxCode ?? '' },
          { op: 'replace', path: '/taxRate', value: line.taxRate ?? 0 },
        ];

        return axios.patch(`/api/RfqVendorBidSheet/${line.vendorLineItemId}`, patchPayload);
      });

      await Promise.all(patchPromises);

      toast.success('Changes saved successfully.');
      setGridChanged(false);
      setOriginalBidLines(JSON.parse(JSON.stringify(bidLines))); // refresh baseline
    } catch (err) {
      console.error('Failed to save changes:', err);
      toast.error('Error saving changes to bid sheet.');
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      await axios.put(`/api/RfqHeader/rfq-approval-needed?useProduction=${useNetSuiteProductionRfq}`, 
      {
        id: rfqId,
        readyForApproval: true,
      });
      toast.success('RFQ submitted for approval.');
      if (onSubmitForApproval) onSubmitForApproval(rfqId);
    } catch (err) {
      toast.error('Failed to submit RFQ for approval.');
      console.error(err);
    } finally {
      setSubmitConfirmDialogOpen(false);
    }
  };

  const handleValidationConfirmation = async () => {
    await handleSaveChanges();

    const results = validateRfqSubmission();
    if (results.length > 0) {
      setValidationMessages(results);
      setValidateDialogOpen(true);
    } else {
      await handleSubmitForApproval();
    }
  };

  const validateRfqSubmission = () => {
    const messages = [];

    for (const line of rfqPoLines) {
      const relatedBids = bidLines.filter((b) => b.poLineItemId === line.id);
      const awarded = relatedBids.filter((b) => b.vendorIsAwarded);

      // Check: Missing awards
      if (awarded.length === 0) {
        messages.push(
          `Line #${line.lineItemNumber} | Description: ${line.description} => No vendor awarded this item.`
        );
      }

      // Check: Multiple vendors awarded
      if (awarded.length > 1) {
        messages.push(
          `Line #${line.lineItemNumber} | Description: ${line.description} => Awarded to multiple vendors.`
        );
      }

      // Check: Total awarded quantity mismatch
      const totalAwarded = awarded.reduce((sum, b) => sum + (b.awardedVendorQuantity ?? 0), 0);
      if (totalAwarded !== line.quantity) {
        messages.push(
          `Line #${line.lineItemNumber} | Description: ${line.description} => Awarded quantity ${totalAwarded} does not match desired quantity of ${line.quantity}.`
        );
      }
    }

    return messages;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xxl">
      <DialogContent sx={{ padding: '16px 24px', minHeight: '80vh' }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h6" fontWeight="bold">
              RFQ Bid Sheet
            </Typography>
          </Grid>
          <Grid item>
            <Button onClick={onClose} variant="contained" color="secondary" size="small">
              Close
            </Button>
          </Grid>
        </Grid>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: '#f9f9f9',
                borderLeft: '4px solid #1976d2',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    RFQ Name
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                    {header?.name || '—'}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {header?.description || '—'}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    Form Type
                  </Typography>
                  <Typography variant="body1">{header?.formType || '—'}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    Creation Date
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {header?.creationDate || '—'}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    Required By (Desired Delivery / Service Completion Date)
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {header?.desiredDeliveryDate || '—'}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    Vendor Instructions
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {header?.vendorInstructions || '—'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleDownloadAttachments}
                disabled={vendors.length === 0 || !rfqId || !header.description}
              >
                Download Attachments
              </Button>

              <Typography variant="subtitle1" fontWeight="bold">
                Total Awarded Amount + Tax:&nbsp;{priceFormatter({ value: totalAwardedAmountWithTax })}
              </Typography>
            </Box>

            <div style={{ height: '45vh', width: '100%' }}>
              <AgGridReact
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={bidLines}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                }}
                autoGroupColumnDef={autoGroupColumnDef}
                groupRowRendererParams={groupRowRendererParams}
                rowSelection={rowSelection}
                groupDisplayType={'singleColumn'}
                suppressAggFuncInHeader={true}
                singleClickEdit={true}
                enterNavigatesVertically={true}
                enterNavigatesVerticallyAfterEdit={true}
                stopEditingWhenCellsLoseFocus={true}
                suppressScrollOnNewData={true}
                onCellValueChanged={handleCellChange}
                onSelectionChanged={handleSelectionChanged}
                onFirstDataRendered={handleFirstDataRendered}
              />
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '12px 24px' }}>
        <Button variant="contained" color="primary" disabled={!gridChanged} onClick={handleSaveChanges}>
          Save Changes
        </Button>
        <Button variant="contained" color="success" onClick={() => setSubmitConfirmDialogOpen(true)}>
          Submit for Approval
        </Button>
      </DialogActions>
      <ConfirmDialog
        open={submitConfirmDialogOpen}
        message="Are you sure you want to submit this RFQ for approval?"
        loading={false}
        onConfirm={handleValidationConfirmation}
        onCancel={() => {
          handleSaveChanges(); // Save but don't submit
          setSubmitConfirmDialogOpen(false);
        }}
        onClose={() => setSubmitConfirmDialogOpen(false)}
        showNo={true}
        showCancel={true}
        confirmLabel="Yes"
        noLabel="No, but save progress"
        cancelLabel="Cancel"
      />
      <ValidateDialog
        open={validateDialogOpen}
        validationResults={validationMessages}
        onConfirm={() => {
          setValidateDialogOpen(false);
          handleSubmitForApproval();
        }}
        onClose={() => setValidateDialogOpen(false)}
      />
    </Dialog>
  );
};

export default RfqBidSubmissionReviewModal;
